import { formatDateTime, normalizeSelection, SelectOption } from '../../components/SalesReport'
import { CourtesyMode, SalesReportFilterOptions, SalesReportGroupBy, SalesReportRequest } from '../../hooks/sales-report'

export type { CourtesyMode, SalesReportGroupBy, SalesReportRequest } from '../../hooks/sales-report'

export interface SalesReportFilterState {
  periodStart: string
  periodEnd: string
  sectorIds: string[]
  ticketIds: string[]
  pdvIds: string[]
  lotIds: string[]
  groupBy: SalesReportGroupBy
  courtesy: CourtesyMode
  showStock: boolean
  includeCommissioners: boolean
  showFee: boolean
  showPaymentMethod: boolean
}

export const DEFAULT_FILTERS: SalesReportFilterState = {
  periodStart: '',
  periodEnd: '',
  sectorIds: [],
  ticketIds: [],
  pdvIds: [],
  lotIds: [],
  groupBy: 'sector',
  courtesy: 'show',
  showStock: true,
  includeCommissioners: true,
  showFee: false,
  showPaymentMethod: false,
}

export const MAX_SELECTED_IDS = 100

export const GROUP_BY_OPTIONS: Array<{ value: SalesReportGroupBy, label: string }> = [
  { value: 'none', label: 'Nenhum' },
  { value: 'sector', label: 'Setor' },
  { value: 'ticket', label: 'Ingresso' },
  { value: 'lot', label: 'Lote' },
  { value: 'pdv', label: 'Ponto de Venda' },
  { value: 'payment', label: 'Forma de Pagamento' },
  { value: 'commissioner', label: 'Comissários' },
  { value: 'commissionerPayment', label: 'Comissários x Pagamento' },
]

export const groupsByPayment = (groupBy: SalesReportGroupBy): boolean =>
  groupBy === 'payment' || groupBy === 'commissionerPayment'

export const groupsByCommissioner = (groupBy: SalesReportGroupBy): boolean =>
  groupBy === 'commissioner' || groupBy === 'commissionerPayment'

export const COURTESY_OPTIONS: Array<{ value: CourtesyMode, label: string }> = [
  { value: 'show', label: 'Exibir' },
  { value: 'hide', label: 'Ocultar' },
  { value: 'only', label: 'Somente' },
]

export const sectorOptions = (options: SalesReportFilterOptions): SelectOption[] => {
  const sessions = new Set(options.sectors.map((sector) => sector.sessionAt).filter((at) => at != null))
  const showSession = sessions.size > 1
  return options.sectors.map((sector) => ({
    value: String(sector.id),
    label: showSession && sector.sessionAt ? `${sector.name} — ${formatDateTime(sector.sessionAt)}` : sector.name,
  }))
}

const ticketsInSectors = (options: SalesReportFilterOptions, sectorIds: string[]): SalesReportFilterOptions['tickets'] => {
  const selected = new Set(sectorIds)
  return options.tickets.filter((ticket) => selected.size === 0 || selected.has(String(ticket.sectorId)))
}

export const ticketOptions = (options: SalesReportFilterOptions, sectorIds: string[]): SelectOption[] => {
  const sectorNames = new Map(options.sectors.map((sector) => [String(sector.id), sector.name] as [string, string]))
  const visible = ticketsInSectors(options, sectorIds)
  const nameCount = visible.reduce(
    (count, ticket) => count.set(ticket.name, (count.get(ticket.name) ?? 0) + 1),
    new Map<string, number>()
  )
  return visible.map((ticket) => {
    const sectorName = sectorNames.get(String(ticket.sectorId))
    const homonym = (nameCount.get(ticket.name) ?? 0) > 1
    return {
      value: String(ticket.id),
      label: homonym && sectorName ? `${ticket.name} · ${sectorName}` : ticket.name,
    }
  })
}

export const pdvOptions = (options: SalesReportFilterOptions): SelectOption[] =>
  options.pdvs.map((pdv) => ({ value: String(pdv.id), label: pdv.name }))

export const lotOptions = (options: SalesReportFilterOptions): SelectOption[] =>
  options.lots.map((lot) => ({ value: String(lot.id), label: lot.name }))

export const withSectors = (
  state: SalesReportFilterState,
  sectorIds: string[],
  options: SalesReportFilterOptions
): SalesReportFilterState => {
  const fitting = new Set(ticketsInSectors(options, sectorIds).map((ticket) => String(ticket.id)))
  return { ...state, sectorIds, ticketIds: state.ticketIds.filter((ticketId) => fitting.has(ticketId)) }
}

export const withAvailableSelections = (
  state: SalesReportFilterState,
  options: SalesReportFilterOptions
): SalesReportFilterState => {
  const sectorIds = normalizeSelection(state.sectorIds, sectorOptions(options))
  return {
    ...state,
    sectorIds,
    ticketIds: normalizeSelection(state.ticketIds, ticketOptions(options, sectorIds)),
    pdvIds: normalizeSelection(state.pdvIds, pdvOptions(options)),
    lotIds: normalizeSelection(state.lotIds, lotOptions(options)),
  }
}

const LOCAL_DATETIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/

export const toWallClockMinutes = (local: string): number | null => {
  const parts = LOCAL_DATETIME.exec(local)
  if (parts == null) return null
  const [, year, month, day, hour, minute] = parts.map(Number)
  return Date.UTC(year, month - 1, day, hour, minute) / 60000
}

export const fromWallClockMinutes = (minutes: number): string => {
  const date = new Date(minutes * 60000)
  const pad = (value: number): string => String(value).padStart(2, '0')
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}T${pad(
    date.getUTCHours()
  )}:${pad(date.getUTCMinutes())}:00`
}

const shortDateTime = (local: string): string => {
  const parts = LOCAL_DATETIME.exec(local)
  if (parts == null) return local
  const [, , month, day, hour, minute] = parts
  return `${day}/${month} ${hour}:${minute}`
}

export const periodLabel = (state: Pick<SalesReportFilterState, 'periodStart' | 'periodEnd'>): string | null => {
  if (state.periodStart !== '' && state.periodEnd !== '') {
    return `${shortDateTime(state.periodStart)} a ${shortDateTime(state.periodEnd)}`
  }
  if (state.periodStart !== '') return `A partir de ${shortDateTime(state.periodStart)}`
  if (state.periodEnd !== '') return `Até ${shortDateTime(state.periodEnd)}`
  return null
}

export const selectionLabel = (ids: string[], options: SelectOption[], singular: string, plural: string): string | null => {
  const chosen = options.filter((option) => ids.includes(option.value))
  if (chosen.length === 0) return null
  return chosen.length === 1 ? `${singular}: ${chosen[0].label}` : `${plural}: ${chosen.length}`
}

export const activeFilterLabels = (state: SalesReportFilterState, options: SalesReportFilterOptions): string[] => {
  const applied = withAvailableSelections(state, options)
  const labels = [
    periodLabel(applied),
    selectionLabel(applied.sectorIds, sectorOptions(options), 'Setor', 'Setores'),
    selectionLabel(applied.ticketIds, ticketOptions(options, applied.sectorIds), 'Ingresso', 'Ingressos'),
    selectionLabel(applied.pdvIds, pdvOptions(options), 'PDV', 'PDVs'),
    selectionLabel(applied.lotIds, lotOptions(options), 'Lote', 'Lotes'),
    applied.courtesy === 'hide' ? 'Sem cortesias' : applied.courtesy === 'only' ? 'Só cortesias' : null,
    applied.includeCommissioners || groupsByCommissioner(applied.groupBy) ? null : 'Sem comissários',
  ]
  return labels.filter((label): label is string => label !== null)
}

export const groupByLabel = (groupBy: SalesReportGroupBy): string =>
  GROUP_BY_OPTIONS.find((option) => option.value === groupBy)?.label ?? groupBy

export const periodError = (state: Pick<SalesReportFilterState, 'periodStart' | 'periodEnd'>): string | null => {
  const start = toWallClockMinutes(state.periodStart)
  const end = toWallClockMinutes(state.periodEnd)
  if (start == null || end == null) return null
  return start > end ? 'O início do período precisa ser antes do fim.' : null
}

const idList = (values: string[]): number[] | undefined => (values.length === 0 ? undefined : values.map(Number))

export const toSalesReportRequest = (
  state: SalesReportFilterState,
  permissions: { canSeeFee: boolean }
): SalesReportRequest => {
  const start = toWallClockMinutes(state.periodStart)
  const end = toWallClockMinutes(state.periodEnd)
  const request: SalesReportRequest = {
    courtesy: state.courtesy,
    includeCommissioners: state.includeCommissioners || groupsByCommissioner(state.groupBy),
    showFee: permissions.canSeeFee && state.showFee,
    showPaymentMethod: state.showPaymentMethod && !groupsByPayment(state.groupBy),
    groupBy: state.groupBy,
  }
  if (start != null) request.periodStart = fromWallClockMinutes(start)
  if (end != null) request.periodEndExclusive = fromWallClockMinutes(end + 1)
  const sectorIds = idList(state.sectorIds)
  const ticketIds = idList(state.ticketIds)
  const pdvIds = idList(state.pdvIds)
  const lotIds = idList(state.lotIds)
  if (sectorIds) request.sectorIds = sectorIds
  if (ticketIds) request.ticketIds = ticketIds
  if (pdvIds) request.pdvIds = pdvIds
  if (lotIds) request.lotIds = lotIds
  return request
}
